import type {
  CodeableConcept,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import {
  codeFromCodeableConcept,
  displayFromCodeableConcept,
} from '@seen/core/codeableConcept'
import { IDT } from '@seen/core/models/care-teams/idt'
import { isOfType, now } from '@seen/core/utils'
import { Badge, TypographyDetail, TypographySubtle } from '@seen/ui'
import { IconArrowRight } from '@tabler/icons-react'
import { differenceInHours, isAfter } from 'date-fns'
import { groupBy, orderBy } from 'lodash-es'
import { type JSX, useMemo } from 'react'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { HoverPractitioner } from '#src/components/core/hover-card/HoverPractitioner.tsx'
import { PractitionerNameplate } from '#src/components/nameplates/PractitionerNameplate.tsx'
import { useResourceVersions } from '#src/hooks/useResourceVersions.ts'

interface RoleTransition {
  role: CodeableConcept[]
  from?: Reference<Practitioner>
  to: Reference<Practitioner>
  // The membership transition instant (period boundary).
  time: string
  // When the change was actually written, derived from version history.
  recordedAt?: string
  author?: Reference
}

function membershipKey(
  practitioner: Reference<Practitioner>,
  roleCode: string | undefined,
  start: string | undefined
): string {
  return `${practitioner.reference}|${roleCode}|${start}`
}

// Membership timeline derived from the care team's participant periods,
// grouped into one entry per role transition. Version history attributes
// each transition to the user who recorded it and exposes backdating.
export function CareTeamHistory({ idt }: { idt: IDT }): JSX.Element {
  const { data: versions } = useResourceVersions(IDT, idt.id)

  const recordedByKey = useMemo(() => {
    const map = new Map<string, { recordedAt: string; author: Reference }>()
    for (const version of orderBy(versions ?? [], (v) => v.lastUpdated)) {
      for (const entry of version.memberHistory) {
        const key = membershipKey(
          entry.practitioner,
          codeFromCodeableConcept(entry.role[0]),
          entry.period?.start
        )
        if (!map.has(key)) {
          map.set(key, {
            recordedAt: version.lastUpdated,
            author: version.lastUpdatedBy,
          })
        }
      }
    }
    return map
  }, [versions])

  const transitions = useMemo(() => {
    const byRole = groupBy(idt.memberHistory, (entry) =>
      codeFromCodeableConcept(entry.role[0])
    )
    const all: RoleTransition[] = []
    for (const [roleCode, entries] of Object.entries(byRole)) {
      for (const entry of entries) {
        if (!entry.period?.start) {
          continue
        }
        // The predecessor is the member whose period ended exactly when this
        // one started; setMemberByRole writes both boundaries from one asOf.
        const predecessor = entries.find(
          (candidate) => candidate.period?.end === entry.period?.start
        )
        const recorded = recordedByKey.get(
          membershipKey(entry.practitioner, roleCode, entry.period.start)
        )
        all.push({
          role: entry.role,
          from: predecessor?.practitioner,
          to: entry.practitioner,
          time: entry.period.start,
          recordedAt: recorded?.recordedAt,
          author: recorded?.author,
        })
      }
    }
    return orderBy(all, (transition) => transition.time, 'desc')
  }, [idt, recordedByKey])

  if (transitions.length === 0) {
    return (
      <TypographySubtle className="text-foreground-secondary">
        No membership history
      </TypographySubtle>
    )
  }

  const currentTime = now()

  return (
    <div className="flex flex-col">
      {transitions.map((transition) => {
        const upcoming = isAfter(transition.time, currentTime)
        const backdated =
          !upcoming &&
          !!transition.recordedAt &&
          differenceInHours(transition.recordedAt, transition.time) > 1
        return (
          <div
            key={`${transition.to.reference}-${codeFromCodeableConcept(transition.role[0])}-${transition.time}`}
            className="flex flex-col gap-1.5 border-b py-3 last:border-b-0"
          >
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">
                {displayFromCodeableConcept(transition.role[0])}
              </span>
              {upcoming && (
                <Badge variant="outline" color="blue">
                  Upcoming
                </Badge>
              )}
              {backdated && (
                <Badge variant="outline" color="yellow">
                  Backdated
                </Badge>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {transition.from ? (
                <PractitionerNameplate
                  practitioner={transition.from}
                  className="text-foreground-secondary line-through"
                />
              ) : (
                <TypographySubtle className="text-foreground-secondary">
                  Unassigned
                </TypographySubtle>
              )}
              <IconArrowRight className="size-4 shrink-0 text-muted-foreground" />
              <PractitionerNameplate practitioner={transition.to} />
            </div>
            <TypographyDetail className="font-normal text-foreground-secondary">
              <HoverDate date={transition.time} format="datetime" />
              {backdated && transition.recordedAt && (
                <>
                  {' · recorded '}
                  <HoverDate date={transition.recordedAt} format="datetime" />
                </>
              )}
              {transition.author &&
                isOfType(transition.author, 'Practitioner') && (
                  <>
                    {' · by '}
                    <HoverPractitioner practitioner={transition.author} />
                  </>
                )}
            </TypographyDetail>
          </div>
        )
      })}
    </div>
  )
}
